/** Matches a trailing ISO timezone designator: "Z" or "+04:00" / "-0400". */
const TZ_DESIGNATOR = /(?:Z|[+-]\d{2}:?\d{2})$/;

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Parse a timestamp coming from the backend into a true instant (Date).
 *
 * The backend stores everything in UTC, but the wire format is inconsistent:
 * tz-aware columns serialize with an offset (`...+00:00`), while naive ones
 * (e.g. metric `time` read back from SQLite) come without any designator.
 * A naive string is therefore pinned to UTC; an explicit offset is respected.
 */
export function parseServerDate(iso: string): Date {
  return new Date(TZ_DESIGNATOR.test(iso) ? iso : `${iso}Z`);
}

/** Format a backend timestamp in the user's local (browser) zone as DD.MM.YYYY HH:mm. */
export function formatLocalDateTime(iso: string): string {
  const d = parseServerDate(iso);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** Format a backend timestamp as a local-time chart hour label (HH:mm). */
export function formatLocalClock(iso: string): string {
  const d = parseServerDate(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Format a backend timestamp as a local-time chart day label (dd.MM). */
export function formatLocalDayMonth(iso: string): string {
  const d = parseServerDate(iso);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`;
}

/** The user's local IANA time zone (e.g. "Asia/Dubai"), used to tell the backend how to bucket. */
export function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}
